import React from 'react';
import { PT } from './types';

interface PTCardTVProps {
  pt: PT;
  onViewDetails?: (pt: PT) => void;
}

const PTCardTV: React.FC<PTCardTVProps> = ({ pt, onViewDetails }) => {
  const progressPercentage = pt.progress_percentage || 0;
  const completeCount = pt.complete_count || 0;
  const totalTasks = pt.total_tasks || 0;
  const teamMembers = pt.team_members || [];
  const sections = pt.section_progress || [];

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getProgressTextColor = (percentage: number): string => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    if (percentage >= 20) return 'text-orange-600';
    return 'text-red-600';
  };

  const getSectionBgColor = (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-100 text-green-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    if (percentage >= 20) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  // Get team leader name
  const getTeamLeader = () => {
    const mainLeaders = ['Nabusfanando', 'Lativa Nurunnisa', 'Weni Eka Putri'];
    const teamLeader = teamMembers.find(m =>
      mainLeaders.some(leader => m.name.toLowerCase().includes(leader.toLowerCase()))
    );
    if (teamLeader) {
      return mainLeaders.find(leader =>
        teamLeader.name.toLowerCase().includes(leader.toLowerCase())
      );
    }
    return null;
  };

  const leaderName = getTeamLeader();

  return (
    <div
      className="h-full bg-white rounded-xl shadow-lg overflow-hidden flex flex-col cursor-pointer hover:shadow-xl transition-shadow"
      onClick={() => onViewDetails?.(pt)}
    >
      {/* Header - BIGGER TEXT */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-white flex-shrink-0">
        <h3 className="font-bold text-xl leading-tight truncate" title={pt.name}>
          {pt.name}
        </h3>
        <div className="flex justify-between items-center mt-1">
          {leaderName ? (
            <span className="text-base text-blue-100 font-medium">TIM - {leaderName}</span>
          ) : (
            <span></span>
          )}
          <span className="bg-white bg-opacity-25 px-3 py-1 rounded-lg text-base font-bold">
            {pt.year}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 flex flex-col min-h-0">
        {/* Overall Progress - BIG */}
        <div className="mb-4">
          <div className="flex justify-between items-end mb-2">
            <span className="text-lg font-semibold text-gray-700">Overall Progress</span>
            <span className={`text-4xl font-bold ${getProgressTextColor(progressPercentage)}`}>
              {progressPercentage.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
            <div
              className={`h-4 rounded-full transition-all duration-500 ${getProgressColor(progressPercentage)}`}
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
          <div className="text-base text-gray-600 text-right mt-1 font-medium">
            {completeCount} / {totalTasks} tasks selesai
          </div>
        </div>

        {/* Section Progress - SIMPLIFIED & BIGGER */}
        {sections.length > 0 && (
          <div className="flex-1 overflow-hidden">
            <p className="text-base font-bold text-gray-700 mb-2">Section Progress</p>
            <div className="grid grid-cols-2 gap-2">
              {sections.slice(0, 6).map((section, idx) => (
                <div
                  key={idx}
                  className={`px-3 py-2 rounded-lg ${getSectionBgColor(section.percentage)}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg truncate flex-1" title={section.name}>
                      {section.name.length > 12 ? section.name.substring(0, 12) + '..' : section.name}
                    </span>
                    <span className="font-bold text-xl ml-2">
                      {section.percentage.toFixed(0)}%
                    </span>
                  </div>
                  <div className="text-sm opacity-75">
                    {section.complete}/{section.total}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Member - Bottom */}
        {teamMembers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 flex-shrink-0">
            <p className="text-base text-gray-600 truncate">
              <span className="font-semibold">Tim:</span> {teamMembers[0].name.split(',')[0]}
              {teamMembers.length > 1 && <span className="text-gray-400"> +{teamMembers.length - 1} lainnya</span>}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PTCardTV;
